import { useEffect, useState } from "react";

const EMPTY = {
  id: "",
  nombre: "",
  municipios: "",
  valor_transporte: "",
  descripcion_cobro: "",
  fecha_entrega: "",
  orden: "100",
  activo: false,
};

function toDraft(destino) {
  return {
    id: destino.id || "",
    nombre: destino.nombre || "",
    municipios: (destino.municipios || []).join("\n"),
    valor_transporte: String(destino.valor_transporte ?? ""),
    descripcion_cobro: destino.descripcion_cobro || "",
    fecha_entrega: destino.fecha_entrega || "",
    orden: String(destino.orden ?? 100),
    activo: Boolean(destino.activo),
  };
}

export function DeliveryDestinationsEditor() {
  const [destinos, setDestinos] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [status, setStatus] = useState("Cargando destinos…");
  const [busy, setBusy] = useState(false);

  async function request(operacion, datos = {}) {
    const response = await fetch("/api/dashboard/destinos-entrega/operacion", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ operacion, datos }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok)
      throw new Error(payload.mensaje || `No fue posible guardar (${payload.codigo || `HTTP ${response.status}`}).`);
    return payload;
  }

  async function load() {
    const payload = await request("listar");
    setDestinos(payload.data?.destinos || []);
  }

  useEffect(() => {
    let active = true;
    load()
      .then(() => active && setStatus(""))
      .catch((error) => active && setStatus(error.message || "No fue posible cargar los destinos."));
    return () => { active = false; };
  }, []);

  function update(key, value) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function prepareMedellin() {
    setDraft({
      ...EMPTY,
      nombre: "Medellín y área metropolitana",
      municipios: "Medellín\nBello\nItagüí\nEnvigado\nSabaneta\nLa Estrella\nCopacabana\nCaldas\nGirardota\nBarbosa",
      valor_transporte: "13000",
      descripcion_cobro: "Transporte y domicilio a Medellín",
      orden: "20",
      activo: false,
    });
    setStatus("Completa la fecha de entrega y guarda cuando quieras preparar Medellín. Permanecerá oculto hasta activarlo.");
  }

  async function save(event) {
    event.preventDefault();
    const municipios = draft.municipios.split("\n").map((item) => item.trim()).filter(Boolean);
    if (!draft.nombre.trim() || !municipios.length || !draft.descripcion_cobro.trim()) {
      setStatus("Completa nombre, municipios y descripción para el cliente.");
      return;
    }
    if (!draft.id && draft.activo && !draft.fecha_entrega) {
      setStatus("Define la fecha fija de entrega antes de activar un destino nuevo.");
      return;
    }
    setBusy(true);
    setStatus("Guardando destino…");
    try {
      const datos = {
        nombre: draft.nombre,
        municipios,
        valor_transporte: Number(draft.valor_transporte || 0),
        descripcion_cobro: draft.descripcion_cobro,
        fecha_entrega: draft.fecha_entrega,
        orden: Number(draft.orden || 100),
        activo: draft.activo,
      };
      await request(draft.id ? "actualizar" : "crear", draft.id ? { ...datos, destino_id: draft.id } : datos);
      await load();
      setDraft(EMPTY);
      setStatus("Destino guardado. Si está activo, aparecerá en Pedidos Web como lugar de entrega.");
    } catch (error) {
      setStatus(error.message || "No fue posible guardar el destino.");
    } finally {
      setBusy(false);
    }
  }

  async function changeState(destino) {
    const next = !destino.activo;
    const action = next ? "activar" : "desactivar";
    if (!window.confirm(`¿${action[0].toUpperCase()}${action.slice(1)} ${destino.nombre}?`)) return;
    setBusy(true);
    setStatus("Actualizando disponibilidad…");
    try {
      await request("cambiar_estado", { destino_id: destino.id, activo: next });
      await load();
      setStatus(next ? "Destino activado y disponible en Pedidos Web." : "Destino oculto para clientes en Pedidos Web.");
    } catch (error) {
      setStatus(error.message || "No fue posible actualizar el destino.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="delivery-destinations-editor">
      <header className="module-panel delivery-destinations-intro">
        <div>
          <p className="eyebrow">DESTINOS DE ENTREGA</p>
          <h2>Ciudades, transporte y fechas</h2>
          <p>Configura dónde recibir pedidos, cuánto cuesta el transporte y la fecha que verá el cliente. Los destinos activos aparecen en Pedidos Web.</p>
        </div>
        <button type="button" className="secondary-action" onClick={prepareMedellin} disabled={busy}>Preparar Medellín</button>
      </header>
      <div className="delivery-destinations-grid">
        <section className="module-panel delivery-destination-list" aria-label="Destinos configurados">
          <h3>Destinos configurados</h3>
          {!destinos.length && <p className="empty-state">Aún no hay destinos configurados.</p>}
          {destinos.map((destino) => (
            <article className="delivery-destination-row" key={destino.id}>
              <div>
                <b>{destino.nombre}</b>
                <small>{destino.descripcion_cobro} · ${Number(destino.valor_transporte || 0).toLocaleString("es-CO")}</small>
                <small>Entrega: {destino.fecha_entrega || "Según disponibilidad actual"}</small>
              </div>
              <div className="delivery-destination-actions">
                <span className={destino.activo ? "destination-state active" : "destination-state"}>{destino.activo ? "Activo" : "Oculto"}</span>
                <button type="button" className="secondary-action" onClick={() => setDraft(toDraft(destino))} disabled={busy}>Editar</button>
                <button type="button" className="secondary-action" onClick={() => changeState(destino)} disabled={busy}>{destino.activo ? "Ocultar" : "Activar"}</button>
              </div>
            </article>
          ))}
        </section>
        <form className="module-panel delivery-destination-form" onSubmit={save}>
          <div className="module-heading">
            <div><p className="eyebrow">{draft.id ? "EDITAR DESTINO" : "NUEVO DESTINO"}</p><h3>{draft.id ? draft.nombre : "Configurar destino"}</h3></div>
            {draft.id && <button className="text-action" type="button" onClick={() => setDraft(EMPTY)}>Nuevo destino</button>}
          </div>
          <label>Nombre visible<input value={draft.nombre} onChange={(event) => update("nombre", event.target.value)} placeholder="Ej. Medellín y área metropolitana" required /></label>
          <label>Municipios o zonas incluidas<textarea value={draft.municipios} onChange={(event) => update("municipios", event.target.value)} placeholder={"Un municipio por línea\nEj. Medellín\nBello"} rows="5" required /><small>Escribe una ciudad o municipio por línea. Podrás cambiar esta lista cuando sea necesario.</small></label>
          <div className="delivery-destination-fields">
            <label>Valor de transporte y domicilio<input type="number" min="0" step="1" value={draft.valor_transporte} onChange={(event) => update("valor_transporte", event.target.value)} required /></label>
            <label>Orden de aparición<input type="number" min="0" max="9999" value={draft.orden} onChange={(event) => update("orden", event.target.value)} required /></label>
          </div>
          <label>Texto que verá el cliente<input value={draft.descripcion_cobro} onChange={(event) => update("descripcion_cobro", event.target.value)} placeholder="Transporte y domicilio a Medellín" required /></label>
          <label>Fecha fija de entrega<input type="date" value={draft.fecha_entrega} onChange={(event) => update("fecha_entrega", event.target.value)} /><small>Para destinos nuevos activos es obligatoria. Supía conserva su calendario actual hasta que definas una fecha fija.</small></label>
          <label className="checkbox-field"><input type="checkbox" checked={draft.activo} onChange={(event) => update("activo", event.target.checked)} /> Activar este destino</label>
          <div className="delivery-destination-form-actions"><button type="submit" disabled={busy}>{busy ? "Guardando…" : draft.id ? "Guardar cambios" : "Crear destino"}</button></div>
          {status && <p className="action-status">{status}</p>}
        </form>
      </div>
    </section>
  );
}
